import React, { useState, useEffect } from 'react';
import { FabrikUser, UserUsageData } from '../../../types';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import CheckinModal from '../../space-ops/components/CheckinModal';

interface UsersTableProps {
  users: FabrikUser[];
  allUsers: FabrikUser[]; // All users for search
  isLoading: boolean;
  isLoadingAllUsers?: boolean; // Optional: Track multi-page loading
  error: string | null;
  refresh: () => void;
  onViewDetails: (userId: string) => void;
  clearSearchTrigger?: number; // Trigger to clear search from parent
  // Note: usage data now comes directly with user objects via usage_data field
}

const UsersTable: React.FC<UsersTableProps> = ({
  users,
  allUsers,
  isLoading,
  isLoadingAllUsers,
  error,
  refresh,
  onViewDetails,
  clearSearchTrigger
}) => {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [filterText, setFilterText] = useState<string>('');
  const [filteredUsers, setFilteredUsers] = useState<FabrikUser[]>(users);
  
  // Check-in modal state
  const [checkinModalOpen, setCheckinModalOpen] = useState<boolean>(false);
  const [selectedUserForCheckin, setSelectedUserForCheckin] = useState<FabrikUser | null>(null);

  // Update filtered users whenever the filter text or allUsers array changes
  useEffect(() => {
    if (!filterText.trim()) {
      setFilteredUsers(users); // Show paginated users when no filter
      return;
    }

    const lowercaseFilter = filterText.toLowerCase();
    const filtered = allUsers.filter(user => {
      // Build combined fields for more robust matching
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim().toLowerCase();
      const reverseFullName = `${user.last_name || ''} ${user.first_name || ''}`.trim().toLowerCase();
      const displayName = (user.display_name || '').toLowerCase();
      const email = (user.email || '').toLowerCase();
      const userId = (user.id || '').toLowerCase();
      const spaceName = (user.space?.name || '').toLowerCase();

      // Aggregate into a single haystack to allow substring search across fields
      const haystack = [fullName, reverseFullName, displayName, email, userId, spaceName]
        .filter(Boolean)
        .join(' ');

      return haystack.includes(lowercaseFilter);
    });
    
    setFilteredUsers(filtered);
  }, [filterText, users, allUsers]);

  // Clear search when triggered by parent component
  useEffect(() => {
    if (clearSearchTrigger !== undefined && clearSearchTrigger > 0) {
      setFilterText('');
    }
  }, [clearSearchTrigger]);

  // Note: Removed auto-clear of search filter to prevent interference with lazy loading
  // Users can manually clear search if needed

  // Lazy load usage data for currently visible users
  useEffect(() => {
    // No-op since usage data now comes directly with user objects
  }, [filteredUsers]);

  // Copy user ID to clipboard
  const copyToClipboard = (e: React.MouseEvent, id: string) => {
    e.stopPropagation(); // Prevent row click event
    navigator.clipboard.writeText(id)
      .then(() => {
        setCopiedId(id);
        // Reset copied state after 2 seconds
        setTimeout(() => setCopiedId(null), 2000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  // Handle filter input change
  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(e.target.value);
  };

  // Check if user can be checked in (has day passes or membership days available)
  const canUserCheckIn = (user: FabrikUser): boolean => {
    // Check if user has active day passes
    const hasActiveDayPasses = Boolean(user.usage_data?.active_day_passes_count && user.usage_data.active_day_passes_count > 0);
    
    // Check if user has available membership days
    // null available_days only means unlimited if they have a membership tier
    const hasAvailableDays = Boolean(
      user.usage_data?.membership_tier && (
        user.usage_data?.available_days === null || // null = unlimited (only if has membership tier)
        Boolean(user.usage_data?.available_days && user.usage_data.available_days > 0)
      )
    );
    
    // Check if user has valid billing
    const hasValidBillingDate = hasValidBilling(user);
    
    return hasActiveDayPasses || (hasAvailableDays && hasValidBillingDate);
  };

  // Helper function to get user's full name
  const getUserName = (user: FabrikUser): string => {
    if (user.first_name || user.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return user.display_name || 'N/A';
  };

  // Helper function to get subscription info
  const getSubscriptionInfo = (user: FabrikUser): string => {
    if (user.subscription_details && user.subscription_details.length > 0) {
      return user.subscription_details[0].product_name || 'Active';
    }
    return 'No memberships active';
  };

  // Helper function to check if billing date is valid
  const hasValidBilling = (user: FabrikUser): boolean => {
    return user.subscription_details?.some(sub => 
      (sub.subscription_status === 'active' || sub.subscription_status === 'trialing') && 
      sub.next_billing_date && 
      !isBillingDatePast(sub.next_billing_date)
    ) || false;
  };

  // Helper function to check if a billing date is in the past
  const isBillingDatePast = (dateString: string | null | undefined): boolean => {
    // If no billing date provided, consider it invalid (past due)
    if (!dateString) {
      return true;
    }
    
    try {
      // Parse date-only values (YYYY-MM-DD) as local dates to avoid timezone shifts
      let billingDate: Date;
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [y, m, d] = dateString.split('-').map(Number);
        billingDate = new Date(y, m - 1, d);
      } else {
        billingDate = new Date(dateString);
      }
      // Check if the date is valid
      if (isNaN(billingDate.getTime())) {
        return true; // Invalid date, consider it past due
      }
      
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
      billingDate.setHours(0, 0, 0, 0);
      return billingDate < today;
    } catch (error) {
      // If any error occurs parsing the date, consider it past due
      return true;
    }
  };

  // Remove client-side sorting: use server-ordered data
  // Sorting removed — now fully server-side
  const displayedUsers = filteredUsers;

  if (isLoading && users.length === 0) {
    return <div className="table-loading">Loading users...</div>;
  }

  if (error && users.length === 0) {
    return (
      <div className="table-error">
        <p>{error}</p>
        <button onClick={refresh} className="retry-button">Retry</button>
      </div>
    );
  }

  if (users.length === 0) {
    return <div className="table-empty">No users found</div>;
  }

  return (
    <div className="users-table-view">
      {isLoading && users.length > 0 && (
        <div className="background-loading">
          <span>Loading...</span>
        </div>
      )}
      
      {error && users.length > 0 && (
        <div className="background-error">
          <p>{error}</p>
          <button onClick={refresh} className="retry-button">Retry</button>
        </div>
      )}
      
      <div className="table-filter">
        <div className="search-input-container">
          <SearchIcon className="search-icon" />
          <input
            type="text"
            placeholder={isLoadingAllUsers ? "Loading all users..." : "Search users by name or email..."}
            value={filterText}
            onChange={handleFilterChange}
            className="search-input"
            disabled={isLoadingAllUsers}
          />
        </div>
        <div className="filter-results">
          {filterText && (
            <span>
              Showing {filteredUsers.length} of {allUsers.length} users
            </span>
          )}
        </div>
      </div>
      
      <div className="user-grid">
        {displayedUsers.length > 0 ? (
          displayedUsers.map(user => (
            <div 
              key={user.id} 
              className="user-card"
              onClick={() => onViewDetails(user.id)}
            >
              {/* Member Badge - top right corner */}
              {(() => {
                const userData = user.usage_data;
                const isCaas = user.caas_data?.is_caas_user;
                
                if (isCaas) {
                  return (
                    <div className="member-badge caas-badge">
                      CaaS
                    </div>
                  );
                }
                
                return userData?.membership_tier ? (
                  <div className="member-badge">
                    Member
                  </div>
                ) : null;
              })()}
              
              <div className="user-avatar">
                {user.image_url ? (
                  <img src={user.image_url} alt={getUserName(user)} />
                ) : (
                  <div className="avatar-placeholder">
                    {getUserName(user).split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                  </div>
                )}
              </div>
              <div className="user-info">
                <div className="user-name">{getUserName(user)}</div>
                <div className="user-details">
                  <span className="user-email">{user.email}</span>
                  {user.space && (
                    <>
                      <span className="separator"> | </span>
                      <span className="user-space">{user.space.name}</span>
                    </>
                  )}
                </div>
                {/* Only show membership badge if not a CaaS user */}
                {!user.caas_data?.is_caas_user && (
                  <div className="user-subscription">
                    <span className={`membership-badge ${getSubscriptionInfo(user).toLowerCase().replace(/\s+/g, '-')}`}>
                      {getSubscriptionInfo(user)}
                    </span>
                  </div>
                )}
                {/* CaaS Team Info */}
                {user.caas_data?.teams && user.caas_data.teams.length > 0 && (
                  <div className="user-caas-info">
                    {user.caas_data.teams.map((team, index) => (
                      <div key={team.team_id} className="caas-team-badge">
                        <span className="caas-label">CaaS Client/Team:</span>
                        <span className="caas-team-name">{team.team_name}</span>
                        <span className="caas-client-name"> • {team.client_name}</span>
                      </div>
                    ))}
                  </div>
                )}
                {/* Day Pass Usage Data */}
                {user.usage_data ? (
                  <div className="user-usage-data">
                    <div className="usage-success">
                      <div className="usage-grid">
                        {/* Day Passes */}
                        <div className="usage-item day-passes">
                          <span className="usage-icon">🎫</span>
                          <div className="usage-details">
                            <span className="usage-label">Day Passes</span>
                            <span className={`usage-value ${user.usage_data.active_day_passes_count > 0 ? 'has-passes' : 'no-passes'}`}>
                              {user.usage_data.active_day_passes_count}
                            </span>
                          </div>
                        </div>
                        
                        {/* Available Days - only show for users with membership tiers */}
                        {user.usage_data.membership_tier && user.usage_data.available_days !== undefined && (
                          <div className="usage-item available-days">
                            <span className="usage-icon">📅</span>
                            <div className="usage-details">
                              <span className="usage-label">Available Days</span>
                              <span className={`usage-value ${
                                user.usage_data.available_days === null ? 'unlimited' : 
                                user.usage_data.available_days > 5 ? 'plenty' :
                                user.usage_data.available_days > 0 ? 'low' : 'none'
                              }`}>
                                {user.usage_data.available_days === null && hasValidBilling(user) ? 'Unlimited' : user.usage_data.available_days}
                              </span>
                            </div>
                          </div>
                        )}
                        
                        {/* Notes Count */}
                        {user.notes_count !== undefined && user.notes_count > 0 && (
                          <div className="usage-item notes">
                            <span className="usage-icon">📝</span>
                            <div className="usage-details">
                              <span className="usage-label">Notes</span>
                              <span className="usage-value" title={`${user.notes_count} note${user.notes_count !== 1 ? 's' : ''}` }>
                                {user.notes_count}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="user-usage-data">
                    <div className="usage-loading">
                      <div className="usage-grid">
                        <div className="usage-item loading">
                          <span className="usage-icon loading-shimmer">⏳</span>
                          <div className="usage-details">
                            <span className="usage-label loading-text">Usage data loading...</span>
                            <div className="usage-loading-bar">
                              <div className="loading-bar-fill"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="user-actions">
                <button 
                  className={`check-in-button ${!canUserCheckIn(user) ? 'disabled' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent card click from triggering
                    if (canUserCheckIn(user)) {
                      onViewDetails(user.id); // Open UserDetailModal instead of CheckinModal
                    }
                  }}
                  disabled={!canUserCheckIn(user)}
                  title={!canUserCheckIn(user) ? 'User has no available check-ins (no day passes or membership days)' : 'Check in this user'}
                >
                  Check In
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="no-users-message">
            {filterText ? 'No users match your search.' : 'No users found.'}
          </div>
        )}
      </div>

      {/* Check-in Modal */}
      <CheckinModal
        open={checkinModalOpen}
        onClose={() => {
          setCheckinModalOpen(false);
          setSelectedUserForCheckin(null);
        }}
        user={selectedUserForCheckin}
        onCheckInSuccess={() => {
          // Refresh user data after successful check-in
          refresh();
        }}
      />
    </div>
  );
};

export default UsersTable;
